import { useTranslation } from 'react-i18next';
import type { ChannexQuarantineAvailabilityPreview } from '@/shared/api';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components';
import { formatDate } from '@/shared/lib/format';

interface Props {
  preview?: ChannexQuarantineAvailabilityPreview;
  loading: boolean;
  ready: boolean;
  error: string | null;
  pending: boolean;
  onClose: () => void;
  onRetry: () => void;
  onConfirm: () => void;
  onReturnFocus?: () => void;
}

export function ReleaseNightsDialog({
  preview,
  loading,
  ready,
  error,
  pending,
  onClose,
  onRetry,
  onConfirm,
  onReturnFocus,
}: Props) {
  const { t } = useTranslation('revisionHolds');
  return (
    <Dialog open onOpenChange={(open) => !open && !pending && onClose()}>
      <DialogContent
        className="overflow-hidden"
        showClose={!pending}
        onCloseAutoFocus={(event) => {
          if (onReturnFocus) {
            event.preventDefault();
            onReturnFocus();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle className="pr-7">{t('previewTitle')}</DialogTitle>
          <DialogDescription>{t('previewDescription')}</DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-3">
          {loading && <p role="status">{t('previewLoading')}</p>}
          {preview && (
            <section aria-label={t('previewScope')} className="space-y-2">
              <p className="text-ink wrap-break-word font-semibold">{preview.propertyGroupName}</p>
              <p className="text-ink-soft text-sm">
                {t('nights', { from: formatDate(preview.from), to: formatDate(preview.to) })}
              </p>
              <ul className="text-ink list-inside list-disc text-sm">
                {preview.apartments.map((apartment) => (
                  <li className="wrap-break-word" key={apartment.id}>
                    {apartment.name}
                  </li>
                ))}
              </ul>
            </section>
          )}
          <p className="border-amber/40 bg-amber/10 text-ink rounded-md border p-3 text-sm">
            {t('previewWarning')}
          </p>
          <p className="text-ink-soft text-sm">{t('previewNoRemoval')}</p>
          {error && (
            <div role="alert" className="text-sealwax text-sm">
              <p>{error}</p>
              <Button
                variant="ghost"
                className="min-h-11"
                onClick={onRetry}
                disabled={loading || pending}
              >
                {t('retry')}
              </Button>
            </div>
          )}
        </DialogBody>
        <DialogFooter className="flex-col items-stretch sm:flex-row sm:items-center">
          <Button variant="ghost" className="min-h-11" onClick={onClose} disabled={pending}>
            {t('cancel')}
          </Button>
          <Button
            variant="brand"
            className="min-h-11 whitespace-normal text-center"
            loading={pending}
            disabled={pending || !ready}
            onClick={onConfirm}
          >
            {t('rederive')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
